// src/pages/shop/ShopWaiterNew.jsx
//
// «Взять заказ» в два шага:
//   Шаг 1 — выбор стола плиткой (или «Другое» — свободный ввод: навынос, веранда).
//   Шаг 2 — меню: набираем позиции и «Принимаем» заказ (уходит на кухню).
// Число столов задаёт владелец на экране официанта; здесь только читаем.

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { products as productsApi, cafeOrders as cafeApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import ScanButton from '../../components/ScanButton';

const DEFAULT_TABLES = 12;

export default function ShopWaiterNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const myShop = useMyShop();

  const [step, setStep] = useState('table'); // 'table' | 'menu'
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [tableLabel, setTableLabel] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tableCount = Number(myShop?.moduleConfig?.tableCount);
  const tables = Number.isFinite(tableCount) && tableCount >= 0 ? tableCount : DEFAULT_TABLES;

  const [busyTables, setBusyTables] = useState(new Set());
  const [openByTable, setOpenByTable] = useState({}); // { '8': {id, number} }
  const [askTable, setAskTable] = useState(null);      // стол, по которому спросили «текущий/новый»
  const [customLabel, setCustomLabel] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const resp = await cafeApi.list('open');
        const open = Array.isArray(resp) ? resp : (resp?.items || []);
        const set = new Set();
        const map = {};
        (open || []).forEach(o => {
          const lbl = String(o.tableLabel || '').trim();
          if (!lbl) return;
          set.add(lbl);
          // если на столе несколько заказов — ведём в самый ранний (первый открытый)
          if (!map[lbl]) map[lbl] = { id: o.id, number: o.number };
        });
        setBusyTables(set);
        setOpenByTable(map);
      } catch (e) { /* не критично */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try { setProducts(await productsApi.list()); }
      catch (e) { show(errorMessage(e, t), 'error'); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => (p.name || '').toLowerCase().includes(q));
  }, [products, search]);

  // Шаг 1 → шаг 2
  const pickTable = (label) => {
    // Стол занят — не заводим молча второй заказ, а спрашиваем.
    if (busyTables.has(label) && openByTable[label]) {
      setAskTable(label);
      return;
    }
    setTableLabel(label);
    setStep('menu');
  };
  const openExisting = () => {
    const o = openByTable[askTable];
    if (o) navigate(`/waiter/order/${o.id}`, { replace: true });
  };
  const createNew = () => {
    setTableLabel(askTable);
    setAskTable(null);
    setStep('menu');
  };
  const confirmCustom = () => {
    const v = customLabel.trim();
    if (!v) { show(t('waiter.errNoTable'), 'error'); return; }
    setTableLabel(v); setStep('menu');
  };

  const addToCart = (p) => {
    setCart(prev => {
      const idx = prev.findIndex(i => i.id === p.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, {
        id: p.id, name: p.name, unit: p.unit || 'шт',
        salePrice: Number(p.salePrice) || 0, costPrice: Number(p.costPrice) || 0, qty: 1,
      }];
    });
  };

  const setQty = (id, delta) => {
    setCart(prev => prev
      .map(i => i.id === id ? { ...i, qty: Math.max(0, i.qty + delta) } : i)
      .filter(i => i.qty > 0));
  };
  const removeItem = (id) => setCart(prev => prev.filter(i => i.id !== id));

  const handleScan = async (code) => {
    try {
      const r = await productsApi.byBarcode(code);
      const p = r?.product;
      if (p) { addToCart(p); show(t('cashier.added', { name: p.name })); }
      else show(t('marking.notOnStock'), 'error');
    } catch { show(t('marking.notOnStock'), 'error'); }
  };

  const total = cart.reduce((s, i) => s + i.salePrice * i.qty, 0);

  const accept = async () => {
    if (!tableLabel.trim()) { show(t('waiter.errNoTable'), 'error'); setStep('table'); return; }
    if (cart.length === 0) { show(t('waiter.errEmpty'), 'error'); return; }
    setSaving(true);
    try {
      const items = cart.map(i => ({
        productId: i.id, name: i.name, qty: i.qty, unit: i.unit,
        salePrice: i.salePrice, costPrice: i.costPrice,
      }));
      const order = await cafeApi.create({ tableLabel: tableLabel.trim(), items, note: note.trim() || undefined });
      show(t('waiter.accepted', { number: order.number }));
      navigate('/waiter', { replace: true });
    } catch (e) {
      show(errorMessage(e, t), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // ШАГ 1 — выбор стола
  // ─────────────────────────────────────────────────────────────
  if (step === 'table') {
    return (
      <div className="page" style={{ paddingBottom: 32 }}>
        {ToastView}
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
          <h1 className="page-title">{t('waiter.pickTable', 'Выберите стол')}</h1>
        </div>

        <div style={{ padding: '0 20px' }}>
          {/* «Другое» сверху — навынос, веранда, доставка */}
          <div className="input-group">
            <label>{t('waiter.tableOther', 'Другое')} <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>· {t('waiter.tableOtherHint', 'навынос, веранда')}</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input" value={customLabel} onChange={e => setCustomLabel(e.target.value)}
                placeholder={t('waiter.tablePlaceholder')} inputMode="text" style={{ flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') confirmCustom(); }}
              />
              <button type="button" className="btn btn-primary" onClick={confirmCustom} style={{ width: 'auto', flexShrink: 0 }}>
                {t('common.next', 'Далее')}
              </button>
            </div>
          </div>

          {/* Плитка столов */}
          <label style={{ display: 'block', marginBottom: 8 }}>{t('waiter.tableLabel')}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8 }}>
            {Array.from({ length: tables }, (_, i) => String(i + 1)).map((label) => {
              const busy = busyTables.has(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => pickTable(label)}
                  style={{
                    position: 'relative', aspectRatio: '1',
                    borderRadius: 'var(--r-md)', border: '1.5px solid var(--border)',
                    background: 'var(--bg-subtle)', color: 'var(--fg)',
                    fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display, inherit)',
                    cursor: 'pointer', transition: 'all .14s',
                  }}
                >
                  {label}
                  {busy && (
                    <span style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 8, height: 8, borderRadius: '50%',
                      background: 'var(--warning, #d98324)',
                    }} title={t('waiter.tableBusy', 'Занят')} />
                  )}
                </button>
              );
            })}
          </div>

          {busyTables.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, color: 'var(--fg-faint)', fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warning, #d98324)' }} />
              {t('waiter.tableBusyHint', 'точка — стол занят')}
            </div>
          )}
        </div>

        {/* Занятый стол: открыть текущий заказ или создать новый счёт */}
        {askTable && (
          <div
            onClick={() => setAskTable(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-elevated, #fff)', width: '100%', borderRadius: '20px 20px 0 0', padding: '22px 20px calc(22px + env(safe-area-inset-bottom))' }}
            >
              <div style={{ width: 40, height: 4, borderRadius: 4, background: 'var(--border)', margin: '0 auto 18px' }} />
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
                {t('waiter.tableBusyTitle', 'Стол {{n}} занят', { n: askTable })}
              </h3>
              <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 18 }}>
                {t('waiter.tableBusyAsk', 'Дописать в текущий заказ или открыть новый счёт?')}
                {openByTable[askTable]?.number ? ` · №${openByTable[askTable].number}` : ''}
              </p>
              <button className="btn btn-primary" onClick={openExisting} style={{ width: '100%', marginBottom: 8 }}>
                {t('waiter.openExisting', 'Открыть текущий заказ')}
              </button>
              <button className="btn btn-ghost" onClick={createNew} style={{ width: '100%' }}>
                {t('waiter.createNew', 'Новый счёт на этот стол')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // ШАГ 2 — меню
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="page" style={{ paddingBottom: cart.length ? 140 : 32 }}>
      {ToastView}
      <div className="page-header">
        <button className="back-btn" onClick={() => setStep('table')}><Icon name="chevronLeft" size={18} /></button>
        <h1 className="page-title">{t('waiter.tableShort', 'Стол')} {tableLabel}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Сменить стол */}
        <button
          type="button" onClick={() => setStep('table')}
          className="card"
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px', marginBottom: 12, border: '1px solid var(--border)', textAlign: 'left' }}
        >
          <Icon name="edit" size={15} />
          <span style={{ flex: 1, fontSize: 13 }}>{t('waiter.tableSelected', 'Выбран стол')}: <b>{tableLabel}</b></span>
          <span style={{ fontSize: 12, color: 'var(--brand-primary)' }}>{t('waiter.changeTable', 'сменить')}</span>
        </button>

        {/* Поиск + скан */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            className="input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('cashier.searchPlaceholder')} style={{ flex: 1 }}
          />
          <ScanButton raw onScan={handleScan} size="md" />
        </div>

        {/* Список товаров */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filtered.map(p => (
              <button
                key={p.id} onClick={() => addToCart(p)}
                className="card"
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', textAlign: 'left', width: '100%', border: '1px solid var(--border)' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{formatMoney(p.salePrice, currency)}</div>
                </div>
                <Icon name="plus" size={18} />
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 13 }}>{t('cashier.noProducts')}</div>
            )}
          </div>
        )}

        {/* Комментарий */}
        <div className="input-group" style={{ marginTop: 14 }}>
          <label>{t('waiter.note')}</label>
          <textarea className="textarea" value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder={t('waiter.notePlaceholder')} />
        </div>
      </div>

      {/* Плавающая корзина снизу */}
      {cart.length > 0 && (
        <div style={{
          position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 12,
          background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)', padding: '12px 20px',
        }}>
          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 8 }}>
            {cart.map(i => (
              <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.name}</div>
                <button className="btn btn-ghost" onClick={() => setQty(i.id, -1)} style={{ width: 'auto', flexShrink: 0, padding: '4px 8px', minHeight: 0 }}><Icon name="minus" size={14} /></button>
                <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{i.qty}</span>
                <button className="btn btn-ghost" onClick={() => setQty(i.id, +1)} style={{ width: 'auto', flexShrink: 0, padding: '4px 8px', minHeight: 0 }}><Icon name="plus" size={14} /></button>
                <span style={{ minWidth: 64, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(i.salePrice * i.qty, currency)}</span>
                <button className="btn btn-ghost" onClick={() => removeItem(i.id)} style={{ width: 'auto', flexShrink: 0, padding: '4px 6px', minHeight: 0, color: 'var(--danger)' }}><Icon name="x" size={14} /></button>
              </div>
            ))}
          </div>
          <button className="btn btn-primary" onClick={accept} disabled={saving} style={{ width: '100%' }}>
            {saving ? '…' : t('waiter.acceptBtn', { total: formatMoney(total, currency) })}
          </button>
        </div>
      )}
    </div>
  );
}
